import { DateTime } from 'luxon';
import type { ZoneOptions } from 'luxon';

/**
 * @public
 * Current PST Date End in milliseconds
 */
export const aDayInMilliseconds = 60 * 60 * 24 * 1000;
export const aWeekInMilliseconds = aDayInMilliseconds * 7;
export const zones = {
  'America/Los_Angeles': 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  UTC: 'UTC',
} as const;

export const getTodayPST = () => {
  return DateTime.local({ zone: zones.PST });
};

export type TimezoneType = (typeof zones)[keyof typeof zones];

export const getDateTimeWithZone = (
  date: Date,
  zone: TimezoneType,
  opts?: ZoneOptions
) => {
  return DateTime.fromJSDate(date).setZone(zone, opts).toJSDate();
};

export const getResetTime = ({
  date,
  unit,
  duration,
  zone,
}: {
  date: Date;
  zone: TimezoneType;
  unit: 'month' | 'year' | 'week' | 'day';
  duration: number;
}) => {
  return DateTime.fromJSDate(date)
    .setZone(zone)
    .startOf(unit)
    .plus({
      [unit]: duration,
    })
    .toJSDate();
};

export const getEndOfMonth = ({
  date,
  offsetHours,
  offsetPeriod,
}: {
  date: Date;
  offsetHours: number;
  offsetPeriod: number;
}) => {
  return (
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offsetPeriod) -
    offsetHours * 3600000
  );
};

export const getEndOfWeek = ({
  date,
  offsetHours,
  offsetPeriod,
}: {
  date: Date;
  offsetHours: number;
  offsetPeriod: number;
}) => {
  const firstUTCDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0,
      0,
      0
    )
  );
  const dateOfMonth = firstUTCDate.getUTCDate();
  const day = (firstUTCDate.getUTCDay() + 6) % 7;
  const last = dateOfMonth - day + 7 * offsetPeriod;
  return (
    new Date(firstUTCDate.setUTCDate(last)).getTime() - offsetHours * 3600000
  );
};

export const getEndOfDate = ({
  date,
  offsetHours,
  offsetPeriod,
}: {
  date: Date;
  offsetHours: number;
  offsetPeriod: number;
}) => {
  return (
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + offsetPeriod,
      0,
      0,
      0
    ) -
    offsetHours * 3600000
  );
};

export const getEndOfYear = ({
  date,
  offsetHours,
  offsetPeriod,
}: {
  date: Date;
  offsetHours: number;
  offsetPeriod: number;
}) => {
  return (
    Date.UTC(date.getUTCFullYear() + offsetPeriod, 0) - offsetHours * 3600000
  );
};

export const toEpoch = (date: Date | string | null | undefined | number) => {
  if (typeof date === 'number') return date;
  if (date instanceof Date) return date.getTime();
  if (date == null) return Number.NaN;
  return Date.parse(date);
};

export const hoursToMs = (hours?: number) => {
  if (hours == null) return 0;
  return hours * 1000 * 3600;
};

export const daysToMs = (days: number) => {
  return hoursToMs(24 * days);
};

export const msToHours = (ms: number) => {
  return ms / 3600 / 1000;
};

export const setPSTZone = (date: string) => {
  return DateTime.fromJSDate(new Date(date)).setZone(zones.PST);
};

export const formatDateTime = (date: string, format: string) => {
  return setPSTZone(date).toFormat(format);
};

export const getToday = () => {
  return DateTime.local({ zone: zones.PST });
};

export const getCurrentPSTDate = () =>
  new Date().toLocaleString('en-US', {
    timeZone: zones.PST,
  });

/**
 * @public
 * Current PST Date Start in milliseconds
 */
export const getCurrentPSTDateStart = () => {
  return getResetTime({
    date: new Date(),
    unit: 'day',
    duration: 0,
    zone: zones.PST,
  });
};

/**
 * @public
 * Current PST Date End in milliseconds
 */
export const getCurrentPSTDateEnd = () => {
  return getResetTime({
    date: new Date(),
    unit: 'day',
    duration: 1,
    zone: zones.PST,
  });
};

export const getCurrentPSTWeekStart = () => {
  return getResetTime({
    date: new Date(),
    unit: 'week',
    duration: 0,
    zone: zones.PST,
  });
};

export const reFormatRelativeTime = (lastUpdate: DateTime) => {
  if (!DateTime.isDateTime(lastUpdate)) {
    return;
  }
  const diffDate = Math.abs(lastUpdate.diffNow().as('days'));
  if (diffDate > 365) {
    return lastUpdate.startOf('year').toRelative();
  }
  if (diffDate > 30) {
    return lastUpdate.startOf('month').toRelative();
  }
  if (diffDate > 7) {
    return lastUpdate.startOf('week').toRelative();
  }
  if (diffDate < 3) {
    return 'Yesterday';
  }
  if (diffDate === 0) {
    const diffHours = lastUpdate.diffNow().as('hours');
    const diffMinutes = lastUpdate.diffNow().as('minutes');

    return `${diffHours} hours ${diffMinutes} mins ago`;
  }

  return lastUpdate.startOf('day').toRelative();
};
